import { newsletterSignupEvent } from 'analytics/events/studio';
import { BaseSyntheticEvent, useCallback, useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { getISOLanguage } from 'shared/utils/locale';
import { useLanguageValue } from 'state/hooks/useLanguage';
import { useStoreValue } from 'state/hooks/useStore';
import { useMutationKlaviyo } from 'state/internal/klaviyo/klaviyo';
import { useRudderAnalytics } from '../providers/RudderAnalytics';

type FieldValues = {
  email: string;
};

const REQUIRED_EMAIL_ERROR = 'Email is required.';
const INVALID_EMAIL_ERROR = 'You have entered an invalid e-mail address. Please try again.';

export const validateEmail = (email: string) => {
  if (!email) {
    return REQUIRED_EMAIL_ERROR;
  }
  if (!/^\S+@\S+\.\S+$/.test(email)) {
    return INVALID_EMAIL_ERROR;
  }
  return false;
};

export const useKlavioEmailSubscription = ({
  location,
  errorMessage,
  eventTitleProp,
  updatedListId
}: {
  location: string;
  errorMessage: string;
  eventTitleProp: string | undefined;
  updatedListId?: string;
}) => {
  const store = useStoreValue();
  const language = useLanguageValue();
  const { mutate, isPending } = useMutationKlaviyo();
  const [isSuccess, setIsSuccess] = useState(false);
  const rudderAnalytics = useRudderAnalytics();

  const {
    control,
    handleSubmit,
    setError,
    watch,
    formState: { errors }
  } = useForm<FieldValues>({
    defaultValues: {
      email: ''
    }
  });

  useEffect(() => {
    const { unsubscribe } = watch((_value) => {
      setIsSuccess(false);
    });
    return () => unsubscribe();
  }, [watch]);

  const emailState = watch('email');

  useEffect(() => {
    const emailError = errors.email?.message;
    if (emailError) {
      newsletterSignupEvent({
        store,
        success: false,
        title: eventTitleProp,
        location,
        email: emailState,
        errorMessage: emailError
      });
    }
  }, [errors, eventTitleProp, location, emailState, store]);

  const handleSubmitValid: SubmitHandler<FieldValues> = useCallback(
    ({ email }) => {
      const emailValidationError = validateEmail(email);
      if (emailValidationError) {
        setError('email', { type: 'manual', message: emailValidationError });
        return;
      }

      mutate(
        { email, store, language: getISOLanguage(language, store), updatedListId },
        {
          onError: () => {
            setError('email', {
              type: 'custom',
              message: errorMessage
            });
            newsletterSignupEvent({
              store,
              success: false,
              title: eventTitleProp,
              location,
              email
            });
          },
          onSuccess: () => {
            setIsSuccess(true);

            if (email) {
              rudderAnalytics?.identify({
                success: true,
                store,
                title: eventTitleProp,
                location,
                email
              });
            }
            newsletterSignupEvent({ store, success: true, title: eventTitleProp, location, email });
          }
        }
      );
    },
    [
      errorMessage,
      language,
      mutate,
      setError,
      store,
      eventTitleProp,
      location,
      updatedListId,
      rudderAnalytics
    ]
  );

  const handleFormSubmit = useCallback(
    (event: BaseSyntheticEvent) => handleSubmit(handleSubmitValid)(event),
    [handleSubmit, handleSubmitValid]
  );

  return { isPending, control, handleFormSubmit, isSuccess, errors };
};
